import { Fragment, type ComponentType } from "react";
import NextLink from "next/link";
import GrMapLocationIcon from "components/icons-svg/GrMapLocationIcon";
import NewmdlocationcityIcon from "components/icons-svg/NewmdlocationcityIcon";
import { popularFallbackDestinations } from "components/searchBar/popularFallbackDestinations";
import { AREA_LEVEL, COUNTRY } from "./types";
type SuggestionVariant = "default" | "home" | "homeNew" | "mobile";
type IconMap = Record<string, ComponentType>;
type SuggestionListWithFallbackProps = {
  addressList: google.maps.places.AutocompletePrediction[];
  onSelect: (item: google.maps.places.AutocompletePrediction) => void;
  iconMap: IconMap;
  defaultIcon: ComponentType;
  variant: SuggestionVariant;
  isLoading?: boolean;
  hasLoaded?: boolean;
};
const getPlaceType = (types: string[]): string => {
  const typeSet = new Set(types);
  if (typeSet.has(COUNTRY)) return "Country";
  if (typeSet.has("locality") || typeSet.has("sublocality") || typeSet.has("sublocality_level_1") || typeSet.has("sublocality_level_2") || typeSet.has("sublocality_level_3") || typeSet.has("sublocality_level_4") || typeSet.has("sublocality_level_5")) {
    return "City";
  }
  if (typeSet.has("administrative_area_level_1")) return "State";
  if (typeSet.has("administrative_area_level_2")) return "Region";
  if (typeSet.has("neighborhood")) return "Neighborhood";
  if (typeSet.has("natural_feature")) return "Natural Feature";
  if (typeSet.has("amusement_park")) return "Amusement Park";
  if (typeSet.has("park")) return "Park";
  const primary = types[0];
  return (AREA_LEVEL as Record<string, string>)[primary] || "Region";
};
const SuggestionListWithFallback = ({
  addressList,
  onSelect,
  iconMap,
  defaultIcon,
  variant,
  isLoading = false,
  hasLoaded = true
}: SuggestionListWithFallbackProps) => {
  const renderItems = () => {
    if (!addressList || addressList.length === 0) return null;
    return <div className='w-full' data-sentry-component="renderItems" data-sentry-source-file="SuggestionListWithFallback.tsx">
        {addressList.map(item => {
        const placeType = getPlaceType(item.types);
        const PlaceIcon = iconMap[placeType] || defaultIcon;
        const showIcon = variant !== "homeNew";
        const showIconCircle = variant === "home";
        const itemClass = variant === "home" ? "p-2.5 hover:bg-[#F8F8F8]" : variant === "homeNew" ? "rounded-[8px] px-4 py-[14.5px] hover:bg-[#E1E3EC]" : variant === "mobile" ? "mb-4 flex min-h-[34px] hover:bg-[#F8F8F8]" : "p-2.5 hover:bg-[#F8F8F8]";
        const titleClass = variant === "homeNew" ? "text-sm leading-[17px] text-[#000] text-start font-semibold" : "text-start text-sm font-semibold text-[#26262B]";
        const subtitleClass = variant === "homeNew" ? "mt-2 text-start text-xs font-medium leading-3 text-[#707281]" : "whitespace-pre-wrap text-start text-xs font-medium text-[#707281]";
        const typeClass = variant === "homeNew" ? "ml-2 text-right text-xs font-semibold leading-3 text-[#707281]" : variant === "home" ? "ml-2 text-right text-sm" : "ml-2 text-right text-sm font-medium text-[#53535D]";
        return <div className={itemClass} key={item.place_id} onClick={() => onSelect(item)} onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            onSelect(item);
          }
        }} role='button' tabIndex={0}>
              <div className='flex w-full cursor-pointer items-center justify-between gap-3'>
                <div className='flex w-full items-start gap-2'>
                  {showIcon && <div className={showIconCircle ? "flex items-center justify-center rounded-full bg-[#e3f6f8] px-[9px] py-[7px]" : variant === "mobile" ? "mt-1" : "mt-1"}>
                      <PlaceIcon />
                    </div>}
                  <div className='flex w-full items-center justify-between'>
                    <div className='flex flex-col'>
                      <p className={titleClass}>
                        {item.structured_formatting.main_text}
                      </p>
                      <p className={subtitleClass}>
                        {item.structured_formatting.secondary_text}
                      </p>
                    </div>
                    <div>
                      <p className={typeClass}>{placeType}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>;
      })}
      </div>;
  };
  const renderFallback = () => {
    if (isLoading || !hasLoaded) return null;
    const headerClass = "mb-2 text-xs font-semibold text-[#707281]";
    const commonWrapper = "border-t border-[#F0F0F0] px-3 pb-2 pt-3";
    const noResults = <div className='p-3 text-center text-sm text-[#53535D]'>
        No results found
      </div>;
    const items = variant === "homeNew" ? popularFallbackDestinations.map(item => <NextLink key={item.path} href={item.path} className='rounded-[8px] px-4 py-[14.5px] hover:bg-[#E1E3EC]'>
              <div className='flex flex-col'>
                <p className='text-sm font-semibold leading-[17px] text-[#000]'>
                  {item.label}
                </p>
                <p className='mt-2 text-xs font-medium leading-3 text-[#707281]'>
                  {item.secondary}
                </p>
              </div>
            </NextLink>) : popularFallbackDestinations.map(item => <NextLink key={item.path} href={item.path} className='flex items-start gap-2 rounded-md px-2 py-2 hover:bg-[#F8F8F8]'>
              <div className={variant === "home" ? "flex items-center justify-center rounded-full bg-[#e3f6f8] px-[9px] py-[7px]" : "mt-1"}>
                {variant === "default" ? <NewmdlocationcityIcon /> : <GrMapLocationIcon />}
              </div>
              <div className='flex flex-col'>
                <p className='text-start text-sm font-semibold text-[#26262B]'>
                  {item.label}
                </p>
                <p className='text-start text-xs font-medium text-[#707281]'>
                  {item.secondary}
                </p>
              </div>
            </NextLink>);
    return <Fragment>
        {noResults}
        <div className={commonWrapper}>
          <p className={headerClass}>Popular picks</p>
          <div className='flex flex-col gap-1'>{items}</div>
        </div>
      </Fragment>;
  };
  return <>
      {addressList && addressList.length > 0 ? renderItems() : renderFallback()}
    </>;
};
export default SuggestionListWithFallback;